import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <div style={pageStyle}>
      <div style={containerStyle}>

        {/* Header */}
        <div style={headerStyle}>
          <p style={kickerStyle}>SAYCIAO.SCHOOL</p>
          <h1 style={titleStyle}>Terms of Service</h1>
          <p style={subtitleStyle}>Effective date: May 1, 2026</p>
          <div style={dividerStyle} />
        </div>

        {/* Intro */}
        <p style={introStyle}>
          Welcome to SayCiao.school. By booking a lesson or using this website, you agree to
          the following terms. Please read them — they're written in plain English and exist
          to make our working relationship clear and fair for both of us.
        </p>

        {/* Section 1 */}
        <Section title="1. Who We Are">
          <p>
            SayCiao.school is a private Italian tutoring service operated by Francesca Muntoni,
            based in Illinois, United States. All lessons are conducted online via video call.
          </p>
        </Section>

        {/* Section 2 */}
        <Section title="2. Booking & Scheduling">
          <p>
            Lessons are booked through the scheduling tool on this website. A booking is confirmed
            once you receive a confirmation email. Francesca reserves the right to decline or
            cancel a booking at her discretion, in which case no payment will be owed.
          </p>
          <p>
            Lesson times are shown in your local timezone as detected by the booking system.
            It is your responsibility to confirm the correct time before booking.
          </p>
        </Section>

        {/* Section 3 */}
        <Section title="3. Payment">
          <p>
            Payment is due <strong>after each lesson</strong>, via Venmo or Wise, unless a
            different arrangement has been agreed upon in writing. Invoices are not issued
            automatically — Francesca will follow up if a payment is outstanding.
          </p>
          <p>
            Rates are agreed upon before lessons begin and may be updated with reasonable
            notice. Continued booking of lessons after a rate change constitutes acceptance
            of the new rate.
          </p>
        </Section>

        {/* Section 4 */}
        <Section title="4. Cancellation Policy">
          <Highlight>
            Cancellations must be made at least <strong>24 hours</strong> before the scheduled
            lesson start time.
          </Highlight>
          <p>
            If you cancel with less than 24 hours' notice, or do not show up to a scheduled
            lesson, the full session fee is still owed. This policy exists out of respect for
            Francesca's time, which has been reserved exclusively for you.
          </p>
          <p>
            Exceptions may be made at Francesca's sole discretion for genuine emergencies.
            If you have an urgent situation, please reach out directly at{' '}
            <a href="mailto:sayciao.school@outlook.com" style={linkStyle}>
              sayciao.school@outlook.com
            </a>{' '}
            as soon as possible.
          </p>
          <p>
            Francesca reserves the right to cancel or reschedule a lesson with reasonable
            notice. In such cases, no payment is owed for the cancelled session.
          </p>
        </Section>

        {/* Section 5 */}
        <Section title="5. Lesson Content & Intellectual Property">
          <p>
            All lesson materials, notes, homework, and resources provided by Francesca are
            for your personal use only. You may not reproduce, distribute, or resell any
            materials without written permission.
          </p>
          <p>
            Lessons may not be recorded without Francesca's explicit prior consent.
          </p>
        </Section>

        {/* Section 6 */}
        <Section title="6. No Guarantee of Results">
          <p>
            Language learning takes time and consistent effort. While Francesca is committed
            to providing high-quality, personalized instruction, she cannot guarantee specific
            outcomes, test scores, or fluency milestones. Progress depends on many factors
            including your dedication, practice outside of lessons, and prior experience.
          </p>
        </Section>

        {/* Section 7 */}
        <Section title="7. Student Conduct">
          <p>
            You agree to engage respectfully in all lessons and communications. Francesca
            reserves the right to end a lesson or terminate the tutoring relationship
            immediately if a student is abusive, disrespectful, or behaves inappropriately,
            with full payment still owed for any completed or late-cancelled sessions.
          </p>
        </Section>

        {/* Section 8 */}
        <Section title="8. Privacy">
          <p>
            Your personal data is handled in accordance with our{' '}
            <Link to="/privacy" style={linkStyle}>Privacy Policy</Link>.
            By using this website and booking lessons, you consent to the collection and use
            of your information as described there.
          </p>
        </Section>

        {/* Section 9 */}
        <Section title="9. Limitation of Liability">
          <p>
            To the fullest extent permitted by law, SayCiao.school and Francesca Muntoni
            shall not be liable for any indirect, incidental, or consequential damages
            arising from the use of this website or participation in lessons. Our total
            liability in any circumstance shall not exceed the amount paid for the most
            recent lesson.
          </p>
        </Section>

        {/* Section 10 */}
        <Section title="10. Governing Law">
          <p>
            These Terms are governed by the laws of the State of Illinois, United States.
            Any disputes shall be resolved in the courts of Illinois, and you consent to
            personal jurisdiction in that state.
          </p>
        </Section>

        {/* Section 11 */}
        <Section title="11. Changes to These Terms">
          <p>
            Francesca may update these Terms from time to time. The latest version will
            always be available at this page. Continued use of the website or booking of
            lessons after changes are posted constitutes your acceptance of the updated Terms.
          </p>
        </Section>

        {/* Contact */}
        <div style={contactBoxStyle}>
          <p style={contactTitleStyle}>Questions?</p>
          <p style={contactTextStyle}>
            If anything here is unclear, just reach out at{' '}
            <a href="mailto:sayciao.school@outlook.com" style={linkStyle}>
              sayciao.school@outlook.com
            </a>
            . Francesca is happy to clarify.
          </p>
        </div>

        {/* Back link */}
        <div style={{ textAlign: 'center', marginTop: '48px' }}>
          <Link to="/" style={backLinkStyle}>← Back to homepage</Link>
        </div>

      </div>
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const Section = ({ title, children }) => (
  <div style={sectionStyle}>
    <h2 style={sectionTitleStyle}>{title}</h2>
    <div style={sectionBodyStyle}>{children}</div>
  </div>
);

const Highlight = ({ children }) => (
  <div style={highlightStyle}>{children}</div>
);

// ─── Styles ───────────────────────────────────────────────────────────────────
const TERRACOTTA = '#BC6C25';
const DEEP_MOSS  = '#283618';
const PAPER      = '#FCF9F2';

const pageStyle = {
  backgroundColor: PAPER,
  minHeight: '100vh',
  padding: '60px 20px 100px',
  fontFamily: "'Montserrat', sans-serif",
};

const containerStyle = {
  maxWidth: '720px',
  margin: '0 auto',
};

const headerStyle = {
  textAlign: 'center',
  marginBottom: '48px',
};

const kickerStyle = {
  fontSize: '0.65rem',
  letterSpacing: '4px',
  color: TERRACOTTA,
  fontWeight: 700,
  marginBottom: '12px',
};

const titleStyle = {
  fontFamily: "'Cormorant Garamond', serif",
  fontSize: 'clamp(2rem, 5vw, 3rem)',
  fontWeight: 400,
  color: DEEP_MOSS,
  margin: '0 0 8px',
};

const subtitleStyle = {
  fontSize: '0.75rem',
  color: '#aaa',
  letterSpacing: '1px',
  margin: 0,
};

const dividerStyle = {
  width: '40px',
  height: '2px',
  backgroundColor: TERRACOTTA,
  margin: '20px auto 0',
  opacity: 0.6,
};

const introStyle = {
  fontSize: '0.9rem',
  lineHeight: '1.8',
  color: '#3d4a2e',
  marginBottom: '40px',
  padding: '20px 24px',
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid rgba(188,108,37,0.1)',
};

const sectionStyle = {
  marginBottom: '36px',
  paddingBottom: '36px',
  borderBottom: '1px solid rgba(188,108,37,0.08)',
};

const sectionTitleStyle = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '2px',
  textTransform: 'uppercase',
  color: TERRACOTTA,
  marginBottom: '14px',
};

const sectionBodyStyle = {
  fontSize: '0.88rem',
  lineHeight: '1.8',
  color: '#3d4a2e',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const highlightStyle = {
  backgroundColor: '#fff8f0',
  border: '1px solid rgba(188,108,37,0.25)',
  borderLeft: `3px solid ${TERRACOTTA}`,
  borderRadius: '8px',
  padding: '14px 18px',
  fontSize: '0.88rem',
  lineHeight: '1.7',
  color: DEEP_MOSS,
};

const contactBoxStyle = {
  backgroundColor: '#fff',
  borderRadius: '14px',
  border: '1px solid rgba(188,108,37,0.12)',
  padding: '28px 32px',
  marginTop: '48px',
  textAlign: 'center',
};

const contactTitleStyle = {
  fontFamily: "'Cormorant Garamond', serif",
  fontSize: '1.4rem',
  fontStyle: 'italic',
  color: DEEP_MOSS,
  margin: '0 0 8px',
};

const contactTextStyle = {
  fontSize: '0.85rem',
  color: '#3d4a2e',
  lineHeight: '1.7',
  margin: 0,
};

const linkStyle = {
  color: TERRACOTTA,
  fontWeight: 600,
  textDecoration: 'none',
  borderBottom: `1px solid rgba(188,108,37,0.3)`,
  paddingBottom: '1px',
};

const backLinkStyle = {
  fontSize: '0.75rem',
  letterSpacing: '1.5px',
  textTransform: 'uppercase',
  color: DEEP_MOSS,
  textDecoration: 'none',
  fontWeight: 700,
  borderBottom: `1px solid rgba(40,54,24,0.2)`,
  paddingBottom: '2px',
};

export default TermsOfService;